/**
 * Sépare, dans un enregistrement de fiche utilisateur, ce qui relève des droits
 * (rôle, libellé et indicateurs du rôle, permissions individuelles, parc) de ce
 * qui relève du profil. Les droits passent par les commandes serveur du
 * registre (accessSetZooEntry…) ; le profil reste écrit directement.
 *
 * Contrat : .scratch/droits-multi-parcs/contrat-registre.md
 */

import { hasWelfareReferentRight } from "./welfareReferent";

const record = (value) => value !== null && typeof value === "object" && !Array.isArray(value);

const roleOf = (right) => (record(right) ? right.value ?? null : typeof right === "string" ? right : null);
const labelOf = (right) => (record(right) ? right.label ?? null : null);
const beaOf = (right) => (record(right) ? right.isBEA === true : false);

const PERMISSIONS = [
  ["vet", (rights) => rights.vet === true || rights.veterinary === true],
  ["technical", (rights) => rights.technical === true],
];

const rightsOf = (profile) => {
  const rights = profile?.rights ?? profile?.userRights;
  return record(rights) ? rights : {};
};

/**
 * @param {object} current fiche serveur actuelle
 * @param {object} next   données que l'écran veut écrire (partielles ou complètes)
 * @returns {{ profile: object, access: object|null, targetZooId: string|null }}
 *   profile : `next` sans aucune modification de droits (prête pour update())
 *   access  : { role?, roleLabel?, roleFlags?, permissions? } à envoyer à accessSetZooEntry, ou null
 *   targetZooId : nouveau parc demandé (déplacement du compte), ou null
 */
export function splitAccessChanges(current = {}, next = {}) {
  const profile = { ...next };
  const access = {};

  // Parc : un déplacement de compte passe par les commandes (ajout, rattachement, retrait).
  const nextZooId = record(next.userZoo) ? next.userZoo.value ?? next.zooId : next.zooId;
  const targetZooId = nextZooId != null && nextZooId !== current.zooId ? nextZooId : null;
  delete profile.zooId;
  delete profile.userZoo;

  // Rôle : valeur, libellé et isBEA sont des droits ; emailSubscription est une préférence.
  if (Object.prototype.hasOwnProperty.call(next, "userRight")) {
    const before = current.userRight;
    const after = next.userRight;
    if (roleOf(after) != null && roleOf(after) !== roleOf(before)) access.role = roleOf(after);
    if (record(after) && labelOf(after) !== labelOf(before) && labelOf(after) != null) access.roleLabel = labelOf(after);
    // isBEA absent = inchangé : la Nouvelle reconstruit le rôle sans lui, et
    // modifier un prénom retirait le droit BEA (audit du 04/10).
    if (record(after) && Object.prototype.hasOwnProperty.call(after, "isBEA") && beaOf(after) !== beaOf(before)) {
      access.roleFlags = { isBEA: beaOf(after) };
    }
    const kept = record(before) ? { ...before } : { value: roleOf(before) };
    if (record(after) && Object.prototype.hasOwnProperty.call(after, "emailSubscription")) {
      kept.emailSubscription = after.emailSubscription;
    }
    profile.userRight = kept;
  }

  // Permissions individuelles ; rights.vetData reste une donnée de profil.
  const permissions = {};
  if (Object.prototype.hasOwnProperty.call(next, "rights") || Object.prototype.hasOwnProperty.call(next, "userRights")) {
    const before = rightsOf(current);
    const after = rightsOf(next);
    for (const [key, read] of PERMISSIONS) {
      if (read(after) !== read(before)) permissions[key] = read(after);
    }
    // « Référent bien-être animal » : seul un `rights.bea` posé par l'écran compte ;
    // tant qu'il est absent de la fiche, l'ancien rôle BEA en tient lieu (#575).
    if (typeof after.bea === "boolean" && after.bea !== hasWelfareReferentRight(current)) permissions.bea = after.bea;
    delete profile.rights;
    delete profile.userRights;
    if (record(after.vetData) && JSON.stringify(after.vetData) !== JSON.stringify(before.vetData)) {
      profile.rights = { ...before, vetData: after.vetData };
    }
  }
  if (Object.prototype.hasOwnProperty.call(next, "canPushToZims")) {
    if ((next.canPushToZims === true) !== (current.canPushToZims === true)) permissions.zimsPush = next.canPushToZims === true;
    delete profile.canPushToZims;
  }
  if (Object.keys(permissions).length) access.permissions = permissions;

  return { profile, access: Object.keys(access).length ? access : null, targetZooId };
}

/** Changements de l'écran « droits » (V2) traduits en permissions du registre. */
export function permissionChangesToAccess(changes = {}) {
  const permissions = {};
  if (Object.prototype.hasOwnProperty.call(changes, "veterinary")) permissions.vet = changes.veterinary === true;
  if (Object.prototype.hasOwnProperty.call(changes, "technical")) permissions.technical = changes.technical === true;
  if (Object.prototype.hasOwnProperty.call(changes, "welfareReferent")) permissions.bea = changes.welfareReferent === true;
  if (Object.prototype.hasOwnProperty.call(changes, "canPushToZims")) permissions.zimsPush = changes.canPushToZims === true;
  return permissions;
}
